import {
  CarrierType,
  CarrierVisitDirection,
  JourneyCategory,
  OrderResponseDto,
  UnitLabelDto,
  UpdateContainerVisitCommand,
} from '@planning/app/api'
import { ContainerJourneyItemStore } from '@planning/pages/Issues/Stores/ContainerJourneyItemStore'
import { ContainerYardOperationViewStore } from '@planning/pages/VesselVisit/Components'
import { ContainerItemStore } from '@planning/rt-stores/container/ContainerItemStore'
import { IOrderItem } from '@planning/rt-stores/order/OrderItem'
import { OrderItemStore } from '@planning/rt-stores/order/OrderItemStore'
import { TenantStore } from '@planning/rt-stores/tenant/TenantStore'
import { containerService } from '@planning/services'
import { formatOog } from '@planning/utils/order-utils'
import { formatStringPosition } from '@tom-ui/utils'
import { action, computed, makeObservable, observable } from 'mobx'
import { OrderFormStore, OrderFormStoreType } from '../V2/stores/OrderFormStore'
import { OrderViewDisplayAttributes } from '../components/ReviewForm/ReviewForm'
import { OrderCargoViewCardAttributes } from '../components/StepperForm/organisms/OrderCargoViewCard'
import { OrderHoldsSectionStore } from '@planning/components/hold/organisms/stores/OrderHoldsSectionStore'
import { OrderPageViewStore } from './OrderPageViewStore'

export class EditOrderPageViewStore extends OrderPageViewStore {
  isLoading = false
  cargoFormIntoEditModeValue = false
  holdsStore = new OrderHoldsSectionStore()

  constructor(
    private orderId: number,
    private formStore: OrderFormStore,
    private tenantStore: TenantStore,
    private containerItemStore: ContainerItemStore,
    private containerJourneyItemStore: ContainerJourneyItemStore,
    private orderItemStore: OrderItemStore,
    public containerYardOperationViewStore: ContainerYardOperationViewStore,
  ) {
    super()
    makeObservable(this, {
      isLoading: observable,
      cargoFormIntoEditModeValue: observable,

      submit: action,
      setIsLoading: action,
      setCargoFormIntoEditMode: action,
      container: computed,
      order: computed,
      journey: computed,
      journeySummary: computed,
      hasServiceOrders: computed,

      displayAttributes: computed,
      initialValues: computed,

      cargoFormIntoEditMode: computed,
    })
  }

  async submit() {
    const cmd = this.mapOrderFormDataToUpdateContainerVisitCommand()
    await containerService.updateContainerVisit(cmd)
  }

  setIsLoading(isLoading: boolean) {
    this.isLoading = isLoading
  }

  setCargoFormIntoEditMode = (isEditMode: boolean) => {
    this.cargoFormIntoEditModeValue = isEditMode
  }

  // Represents the order the user wants to edit, even though we currently display the container journey on the page
  get order() {
    if (!this.orderId) return

    const primaryOrder = this.orderItemStore.elements[this.orderId]?.data
    if (!primaryOrder) return

    if (
      primaryOrder.direction === 'Inbound' &&
      primaryOrder.status === 'Fulfilled' &&
      primaryOrder.linkedOrderId
    ) {
      const linkedOrder = this.orderItemStore.elements[primaryOrder.linkedOrderId]?.data

      if (linkedOrder) {
        return linkedOrder
      }
    }

    return primaryOrder
  }

  get container() {
    if (!this.order?.containerId) return
    return this.containerItemStore.elements[this.order.containerId]?.data
  }

  get cargoFormIntoEditMode() {
    return this.formStore.isRestow && this.cargoFormIntoEditModeValue
  }

  // Represents the container journey of the order
  get journey() {
    if (!this.orderId) return
    //TODO [Order rework] Clarify the correct way to get this info
    return this.orderItemStore.elements[this.orderId]?.containerJourney
  }

  //TODO [Order rework] This might not be needed based on the decision of the method above
  get hasServiceOrders() {
    return !!(this.journey?.data.strippingOrderId || this.journey?.data.stuffingOrderId)
  }

  get journeySummary() {
    //TODO [Order rework] Check if this is correct: If there is no journey, it is probably an outbound order without the inbound part
    const order = this.orderItemStore.elements[this.orderId]

    if (this.journey === undefined) {
      return {
        outboundOrder: order,
        commonOrder: order,
      }
    }

    const inboundOrder = this.journey?.inboundOrder
    const outboundOrder = this.journey?.outboundOrder

    const commonOrder = order

    return {
      inboundOrder,
      outboundOrder,
      commonOrder,
    }
  }

  get displayAttributes(): OrderViewDisplayAttributes {
    const { inboundOrder, outboundOrder, commonOrder } = this.journeySummary

    const inboundVisit = inboundOrder?.visit
    const outboundVisit = outboundOrder?.visit

    const inboundOrderData = inboundOrder?.data
    const outboundOrderData = outboundOrder?.data

    const inboundVesselNumber = this.getVesselNumber(inboundOrderData, inboundVisit)
    const outboundVesselNumber = this.getVesselNumber(outboundOrderData, outboundVisit)

    const attributes = {
      unitAdditionalAttributes: {
        inboundOrder: inboundOrderData,
        outboundOrder: outboundOrderData,
        journey: {
          status: this.journey?.status,
          orderStatus: commonOrder?.data.status,
          operationType: this.journey?.data.operationType,
          inboundCarrierType: this.journey?.inboundOrder?.data.carrierVisitType,
          currentPosition: this.journey?.data.currentPosition,
          equipmentType: this.journey?.data.equipmentType,
          yardLocations: this.journey?.data.yardLocations,
          containerId: this.journey?.data.containerId,
          journeyCategory: this.resolveJourneyCategory(),
        },
        orderNotes: {
          notes: commonOrder?.data?.notes,
          operationalInstructions: commonOrder?.data?.operationalInstructions,
        },
        activeDamages: this.container?.damages,
      },
      containerAttributes: {
        number: this.order?.containerNumber,
        isoCode: this.order?.containerIsoCode,
        unitType: this.order?.unitType,
        length: this.order?.containerLength,
        height: this.order?.containerHeight,
        type: this.order?.containerType,
        tare: this.order?.containerTare,
        maxGross: this.order?.containerMaxGross,
        operator: this.order?.operator,
      },
      transportAttributes: {
        inboundTransport: {
          direction: CarrierVisitDirection.Inbound,
          carrierName: inboundVisit?.data.identifier,
          carrierType: inboundOrderData?.carrierVisitType,
          eta: inboundVisit?.data.eta,
          ata: inboundVisit?.data.ata,
          atd: inboundVisit?.data.atd,
          portOfLoading: inboundOrderData?.portOfLoading,
          dischargeApproval: inboundOrderData?.isApproved ?? false,
          vesselStowagePosition: this.getVesselStowagePosition(inboundOrderData),
          vesselNumber: inboundVesselNumber,

          operator: this.order?.operator,
          numericReleaseId: inboundOrderData?.numericReleaseId,
          numericReleaseReference: inboundOrderData?.numericReleaseReference,
          nonNumericReleaseId: inboundOrderData?.nonNumericOrderId,
          nonNumericReleaseReference: inboundOrderData?.nonNumericReleaseReference,
          closedAt: inboundOrderData?.closedAt ?? null,
        },
        outboundTransport: {
          direction: CarrierVisitDirection.Outbound,
          carrierName: outboundVisit?.data.identifier,
          carrierType: outboundOrderData?.carrierVisitType,
          eta: outboundVisit?.data.eta,
          ata: outboundVisit?.data.ata,
          atd: outboundVisit?.data.atd,
          finalDestination: outboundOrderData?.finalDestination,
          portOfDischarge: outboundOrderData?.portOfDischarge,
          loadApproval: outboundOrderData?.isApproved ?? false,
          vesselStowagePosition: this.getVesselStowagePosition(outboundOrderData),
          vesselNumber: outboundVesselNumber,
          isFinalPosition: !!outboundOrderData?.finalStowagePosition,

          operator: this.order?.operator,
          numericReleaseId: outboundOrderData?.numericReleaseId,
          numericReleaseReference: outboundOrderData?.numericReleaseReference,
          nonNumericReleaseId: outboundOrderData?.nonNumericOrderId,
          nonNumericReleaseReference: outboundOrderData?.nonNumericReleaseReference,
          closedAt: outboundOrderData?.closedAt ?? null,
        },
      },
      cargoAttributes: {
        commonCargoAttributes: this.mapToCargoAttributes(
          commonOrder,
          outboundOrderData?.unitLabel ?? inboundOrderData?.unitLabel,
          outboundOrderData?.status === 'Fulfilled' || inboundOrderData?.status === 'Fulfilled',
          commonOrder?.data?.referenceNumber ||
            inboundOrderData?.referenceNumber ||
            outboundOrderData?.referenceNumber ||
            undefined,
        ),
        inboundCargoAttributes: this.mapToCargoAttributes(inboundOrder),
        outboundCargoAttributes: this.mapToCargoAttributes(outboundOrder),
      },
    }

    return attributes
  }

  get initialValues(): Partial<OrderFormStoreType> {
    const { inboundOrder, outboundOrder, commonOrder } = this.journeySummary

    const commonOrderData = commonOrder?.data
    const inboundOrderData = inboundOrder?.data
    const outboundOrderData = outboundOrder?.data

    const referenceNumber =
      commonOrderData?.referenceNumber ||
      inboundOrderData?.referenceNumber ||
      outboundOrderData?.referenceNumber ||
      ''

    const values: Partial<OrderFormStoreType> = {
      referenceNumber,
      consignee: commonOrderData?.consignee ?? '',
      operationalInstructions: commonOrderData?.operationalInstructions,
      notes: commonOrderData?.notes,
      billOfLadding: commonOrderData?.billOfLading ?? '',

      shippingOrder: commonOrderData?.shippingOrderReference ?? '',
      releaseReference: commonOrderData?.releaseReference ?? '',

      loadApproval: outboundOrder?.data?.isApproved ?? false,
      dischargeApproval: inboundOrder?.data?.isApproved ?? false,

      origin: commonOrderData?.placeOfOrigin ?? '',
      inlandDestination: commonOrderData?.inlandDestination ?? '',
      finalDestination: commonOrderData?.finalDestination ?? '',
      portOfLoading: commonOrderData?.portOfLoading ?? '',
      portOfDischarge: commonOrderData?.portOfDischarge ?? '',

      forwarderId: commonOrderData?.forwarderId ?? null,
      shipperId: commonOrderData?.shipperId ?? null,

      customer: {
        id: inboundOrder?.data?.customerId ?? outboundOrder?.data?.customerId ?? 0,
        name: commonOrderData?.customerName ?? '',
        referenceId: commonOrderData?.customerReferenceId ?? 0,
      },
    }

    if (inboundOrder) {
      values.inboundCargoDetails = this.mapToDirectionalCargoDetails(inboundOrder.data)
    }

    if (outboundOrder) {
      values.outboundCargoDetails = this.mapToDirectionalCargoDetails(outboundOrder.data)
    }

    if (commonOrder) {
      values.cargoDetails = this.mapToDirectionalCargoDetails(commonOrderData)
    }

    return values
  }

  mapToCargoAttributes(
    orderItem?: IOrderItem,
    unitLabel?: UnitLabelDto,
    isOrderFulfilled?: boolean,
    orderReference?: string,
  ): OrderCargoViewCardAttributes {
    const order = orderItem?.data

    const attributes: OrderCargoViewCardAttributes = {
      consignee: order?.consignee ?? '',
      orderReference: orderReference ?? order?.referenceNumber ?? '',
      customerName: order?.customerName ?? '',
      content: order?.content ?? '',
      isEmpty: order?.isEmpty ?? false,
      grossWeight: order?.grossWeight?.toString() ?? '',
      vgm: order?.vgm?.toString() ?? '',
      weightClass: orderItem?.weightClass,
      dangerousGoods: order?.isDangerous,
      customs: order?.customs ?? [],
      hasSeals: (order?.seals?.length ?? 0) > 0 || (order?.hasSeals ?? false),
      oog: order?.isOOG ?? false,
      oogDimensions: formatOog(order),
      unloadingMethod: order?.unloadingMethod,
      imoClasses: order?.imoClasses ?? [],
      doorDirection: order?.doorDirection,
      otherLabels: order?.otherLabels ?? [],
      unitLabel: order?.unitLabel ?? unitLabel,
      stowageInstruction: order?.stowageInstruction
        ? { ...order.stowageInstruction, hasStowageInstruction: true }
        : undefined,
      unNumbers: order?.dangerousGoodsDefinitions ?? [],

      billOfLading: order?.billOfLading ?? null,
      shippingOrderReference: order?.shippingOrderReference ?? null,
      releaseReference: order?.releaseReference ?? '',

      origin: order?.placeOfOrigin ?? null,
      inlandDestination: order?.inlandDestination ?? null,
      finalDestination: order?.finalDestination ?? null,
      portOfLoading: order?.portOfLoading ?? null,
      portOfDischarge: order?.portOfDischarge ?? null,

      isApprovedInbound: order?.direction === 'Inbound' ? (order?.isApproved ?? null) : null,
      isApprovedOutbound: order?.direction === 'Outbound' ? (order?.isApproved ?? null) : null,
      isOrderFulfilled: isOrderFulfilled ?? order?.status === 'Fulfilled',

      shipperId: order?.shipperId,
      forwarderId: order?.forwarderId,
    }

    return attributes
  }

  mapOrderFormDataToUpdateContainerVisitCommand(): UpdateContainerVisitCommand {
    const { inboundOrder, outboundOrder } = this.journeySummary

    const formData = this.formStore.data

    const hasServiceOrder = this.hasServiceOrders

    const commonCargoData = formData.cargoDetails
    const inboundCargoData = hasServiceOrder ? formData.inboundCargoDetails : commonCargoData
    const outboundCargoData = hasServiceOrder ? formData.outboundCargoDetails : commonCargoData

    let stowageInstructionId = null
    if (inboundCargoData?.stowageInstruction?.hasStowageInstruction)
      stowageInstructionId = inboundCargoData?.stowageInstruction?.id
    else if (outboundCargoData?.stowageInstruction?.hasStowageInstruction)
      stowageInstructionId = outboundCargoData?.stowageInstruction?.id

    return {
      inboundOrderId: inboundOrder?.id,
      outboundOrderId: outboundOrder?.id,
      containerId: this.order?.containerId ?? 0,
      containerNumber: this.order?.containerNumber ?? '',
      containerIsoCode: this.order?.containerIsoCode ?? '',
      referenceNumber: formData.referenceNumber,
      notes: formData.notes,
      operationalInstructions: formData.operationalInstructions,
      consignee: formData.consignee,
      customerId: formData.customer?.id,
      inboundTransportData: this.mapToTransportData(
        formData.inboundCargoDetails,
        commonCargoData,
        CarrierVisitDirection.Inbound,
        this.tenantStore.skipReleaseOrder,
      ),
      outboundTransportData: this.mapToTransportData(
        formData.outboundCargoDetails,
        commonCargoData,
        CarrierVisitDirection.Outbound,
        this.tenantStore.skipReleaseOrder,
      ),
      inboundCargo: this.mapToCargoData(inboundCargoData),
      outboundCargo: this.mapToCargoData(outboundCargoData),
      inboundHasSeals: inboundCargoData?.seals?.hasSeals,
      outboundHasSeals: outboundCargoData?.seals?.hasSeals,
      inboundSeals: inboundCargoData?.seals?.seals,
      outboundSeals: outboundCargoData?.seals?.seals,
      outboundCustoms: outboundCargoData?.customs,
      stowageInstructionId: stowageInstructionId ?? null,

      forwarderId: formData.forwarderId,
      shipperId: formData.shipperId,

      billOfLading: formData.billOfLadding,
      shippingOrderReference: formData.shippingOrder,
      releaseReference: formData.releaseReference,

      placeOfOrigin: formData.origin,
      inlandDestination: formData.inlandDestination,
      finalDestination: formData.finalDestination,
      portOfLoading: formData.portOfLoading,
      portOfDischarge: formData.portOfDischarge,

      isApprovedInbound: formData.dischargeApproval,
      isApprovedOutbound: formData.loadApproval,
      holds: this.holdsStore.upsertPayload,
    }
  }

  private getVesselStowagePosition(order?: OrderResponseDto) {
    const stowagePosition = order?.finalStowagePosition ?? order?.stowagePosition

    if (!stowagePosition?.bay || !stowagePosition?.row || !stowagePosition?.tier) {
      return undefined
    }

    return formatStringPosition(
      stowagePosition.bay.toString(),
      stowagePosition.row.toString(),
      stowagePosition.tier.toString(),
    )
  }

  private getVesselNumber(order: OrderResponseDto | undefined, visit: any) {
    if (order?.carrierVisitType === CarrierType.Vessel && visit?.data) {
      return visit.data.number
    }
    return undefined
  }

  private resolveJourneyCategory() {
    const { inboundOrder, outboundOrder, commonOrder } = this.journeySummary
    const inboundOrderData = inboundOrder?.data
    const outboundOrderData = outboundOrder?.data

    const getValidCategory = (cat?: JourneyCategory | null) => {
      return cat && cat !== JourneyCategory.None ? cat : null
    }

    return (
      getValidCategory(commonOrder?.journeyCategory) ??
      getValidCategory(inboundOrderData?.journeyCategory) ??
      getValidCategory(outboundOrderData?.journeyCategory) ??
      null
    )
  }
}
